import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  FormControl,
  CircularProgress,
  Skeleton,
  TextField,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Switch,
  Grid,
  FormControlLabel,
  Tabs,
  Tab,
  Chip,
  Autocomplete,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./AppSettings.css";
import ColorPicker from "./ColorPicker";
import config from "../../config.js";
import { useSettings } from "../../context/SettingsContext";
import { CUSTOM_CHART_CONFIGS } from "../manageData/Analytics/chartConfigs.js";
import { useUser } from "../../pages/login/UserContextPersistent";
import { PAGE_IDS, isJsonDrivenLayoutEnabled } from "../../config/uiLayoutConfig";
import { usePageLayout } from "../../context/LayoutContext";
import {
  buildSettingsTabsFromLayout,
  getLegacySettingsTabs,
} from "../../layout/settingsLayoutUtils";
import Advertisements from "./Advertisements";
import {
  languages,
  SettingRow,
  SettingTextRow,
  SettingRowSkeleton,
  isValidMapViewLayerCombination,
  formatMapLayer,
  tripDurationOptions,
  stopIconOptions,
  chargingPointIconOptions,
  intermediatePointIconOptions,
  originIconOptions,
  destinationIconOptions,
} from "./appConstants";
import MultiColorPicker from "./MultiColorPicker.jsx";
import Application from "./Application.jsx";
import Tracking from "./Tracking.jsx";
import EmailReport from "./EmailReport.jsx";
import ThemeIcon from "./ThemeIcon.jsx";
import Analytics from "./Analytics.jsx";
import ThemePicker from "./ThemePicker.jsx";
import Labels from "./Labels.jsx";
import { getDefaultApplicationOptions } from "../../utils/defaultApplication";
import { isSettingsFieldVisible } from "../../config/settingsFieldAccess";

const languageChangeDurationOptions = [
  { value: 5, label: "5 seconds" },
  { value: 10, label: "10 seconds" },
  { value: 15, label: "15 seconds" },
  { value: 20, label: "20 seconds" },
  { value: 30, label: "30 seconds" },
  { value: 45, label: "45 seconds" },
  { value: 60, label: "1 minute" },
  { value: 120, label: "2 minutes" },
  { value: 180, label: "3 minutes" },
  { value: 300, label: "5 minutes" },
];

const reportFrequencyOptions = [
  { value: 15, label: "15 Days" },
  { value: 30, label: "30 Days" },
  { value: 45, label: "45 Days" },
  { value: 60, label: "60 Days" },
  { value: 90, label: "90 Days" },
  { value: 180, label: "6 Months" },
  { value: 365, label: "1 Year" },
];

const reportTypeOptions = [
  { value: "activity", label: "Activity Reports" },
  { value: "overspeed", label: "Overspeed Reports" },
  { value: "sos", label: "SOS Reports" },
  { value: "runtime", label: "Runtime Reports" },
  { value: "trip", label: "Trip Reports" },
  { value: "inventory", label: "Inventory Reports" },
  { value: "routeDeviation", label: "Route Deviation Reports" },
  { value: "halt", label: "Halt Reports" },
  { value: "entryExit", label: "Entry/Exit Reports" },
];

const inactiveTimeOptions = [
  { value: 1, label: "1 minute" },
  { value: 2, label: "2 minutes" },
  { value: 3, label: "3 minutes" },
  { value: 5, label: "5 minutes" },
  { value: 10, label: "10 minutes" },
  { value: 15, label: "15 minutes" },
  { value: 20, label: "20 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 45, label: "45 minutes" },
  { value: 60, label: "60 minutes" },
];

const deadTimeOptions = [
  { value: 1, label: "1 minute" },
  { value: 2, label: "2 minutes" },
  { value: 3, label: "3 minutes" },
  { value: 5, label: "5 minutes" },
  { value: 10, label: "10 minutes" },
  { value: 15, label: "15 minutes" },
  { value: 20, label: "20 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 45, label: "45 minutes" },
  { value: 60, label: "60 minutes" },
  { value: 90, label: "90 minutes" },
  { value: 120, label: "120 minutes" },
];

const backUpDuration = [
  { value: 1, label: "1 Day" },
  { value: 3, label: "3 Days" },
  { value: 7, label: "7 Days" },
  { value: 30, label: "30 Days" },
  { value: 90, label: "90 Days" },
  { value: 180, label: "180 Days" },
  { value: 365, label: "365 Days" },
];

const vehicleStatusOptions = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  // { value: "dead", label: "Dead" },
];

const inactiveVehicleTimeoutOptions = [
  { value: 5, label: "5 minutes" },
  { value: 10, label: "10 minutes" },
  { value: 15, label: "15 minutes" },
  { value: 20, label: "20 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 45, label: "45 minutes" },
  { value: 60, label: "60 minutes" },
];

const distanceThresholdOptions = [
  { value: 50, label: "50 meters" },
  { value: 100, label: "100 meters" },
  { value: 150, label: "150 meters" },
  { value: 200, label: "200 meters" },
  { value: 250, label: "250 meters" },
  { value: 300, label: "300 meters" },
  { value: 500, label: "500 meters" },
  { value: 1000, label: "1 kilometer" },
];

const averageStopTimeOptions = [
  { value: 1, label: "1 minute" },
  { value: 2, label: "2 minutes" },
  { value: 3, label: "3 minutes" },
  { value: 5, label: "5 minutes" },
  { value: 10, label: "10 minutes" },
  { value: 15, label: "15 minutes" },
];

const arrivingStatusDistanceOptions = [
  { value: 50, label: "50 meters" },
  { value: 100, label: "100 meters" },
  { value: 150, label: "150 meters" },
  { value: 200, label: "200 meters" },
  { value: 300, label: "300 meters" },
  { value: 500, label: "500 meters" },
];

const historyPointsMarkerOptions = [
  { value: 30, label: "30 Seconds" },
  { value: 0.5, label: "30 Seconds" },
  { value: 1, label: "1 Minute" },
  { value: 2, label: "2 Minutes" },
  { value: 3, label: "3 Minutes" },
  { value: 5, label: "5 Minutes" },
];

const warningMarginOptions = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5" },
  { value: 6, label: "6" },
  { value: 7, label: "7" },
  { value: 8, label: "8" },
  { value: 9, label: "9" },
  { value: 10, label: "10" },
];

const deviceInactiveMinutesOptions = [
  { value: 3, label: "3 minutes" },
  { value: 5, label: "5 minutes" },
  { value: 7, label: "7 minutes" },
  { value: 10, label: "10 minutes" },
  { value: 15, label: "15 minutes" },
  { value: 20, label: "20 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 40, label: "40 minutes" },
  { value: 50, label: "50 minutes" },
  { value: 60, label: "60 minutes" },
];

const historyPointDistanceOptions = [
  { value: 100, label: "100 m" },
  { value: 200, label: "200 m" },
  { value: 500, label: "500 m" },
  { value: 1000, label: "1 km" },
];

const SETTINGS_LOG_PREFIX = "[AppSettings]";
const ANALYTICS_ONLY_KEYS = [
  "selectedSite",
  "reportFrequencyDays",
  "selectedReportTypes",
  "selectedGraphs",
  "selectedReceivers",
];

const areSettingValuesEqual = (formValue, originalValue) => {
  if (Array.isArray(formValue) && Array.isArray(originalValue)) {
    return (
      JSON.stringify([...formValue].sort()) ===
      JSON.stringify([...originalValue].sort())
    );
  }

  return formValue === originalValue;
};

const AppSettings = ({ school = false } = {}) => {
  const { t, i18n } = useTranslation();
  const { state, organizationInactive } = useUser();
  const navigate = useNavigate();
  const { settings, loading, updateSettings, fetchSettings } = useSettings();
  const [checked, setChecked] = useState(Array(18).fill(false));
  const [sites, setSites] = useState([]);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [inputValue, setInputValue] = useState("");
  const [emailError, setEmailError] = useState("");
  const [organizationUsers, setOrganizationUsers] = useState([]);

  const toggleCheckbox = (index) => {
    const newChecked = [...checked];
    newChecked[index] = !newChecked[index];
    setChecked(newChecked);
  };

  let organizationId = null;
  try {
    const userStateString = localStorage.getItem("userState");
    if (userStateString) {
      const userState = JSON.parse(userStateString);
      organizationId = userState.user.organizationId;
    }
  } catch (error) {
    console.error("Error retrieving organizationId from localStorage:", error);
  }

  const isSuperAdmin = (() => {
    try {
      const user = JSON.parse(localStorage.getItem("userState"))?.user;
      return user?.organizationId === 5 && user?.email === "sales@routeeye.io";
    } catch {
      return false;
    }
  })();

  const useJsonLayout = isJsonDrivenLayoutEnabled();
  const settingsPageId = school ? PAGE_IDS.SCHOOL_SETTINGS : PAGE_IDS.FLEET_SETTINGS;
  const { layout: moduleLayout } = usePageLayout(useJsonLayout ? settingsPageId : null);

  const isFieldVisible = (fieldKey) =>
    isSettingsFieldVisible(fieldKey, state?.features);

  const enabledSettingsTabs = Array.isArray(settings?.enabledSettingsTabs)
    ? settings.enabledSettingsTabs
    : null;

  const legacyAvailableTabs = useMemo(
    () =>
      getLegacySettingsTabs({
        t,
        isSuperAdmin,
        school,
        features: state?.features,
        enabledSettingsTabs,
      }),
    [t, isSuperAdmin, school, state?.features, enabledSettingsTabs],
  );

  const layoutAvailableTabs = useMemo(() => {
    if (!moduleLayout) return null;
    return buildSettingsTabsFromLayout(moduleLayout, {
      t,
      isSuperAdmin,
      school,
      features: state?.features,
      enabledSettingsTabs,
    });
  }, [moduleLayout, t, isSuperAdmin, school, state?.features, enabledSettingsTabs]);

  const availableTabs = useMemo(() => {
    const tabs = layoutAvailableTabs || legacyAvailableTabs;
    if (tabs.some((tab) => tab.tabKey === "advertisements")) return tabs;
    return [
      ...tabs,
      { tabKey: "advertisements", label: t("Advertisements"), value: 6 },
    ];
  }, [layoutAvailableTabs, legacyAvailableTabs, t]);

  const canAccessAnalyticsSettings = useMemo(
    () =>
      availableTabs.some(
        (tab) => tab.tabKey === "emailReport" || tab.tabKey === "analytics",
      ),
    [availableTabs],
  );

  useEffect(() => {
    if (activeTab >= availableTabs.length) {
      setActiveTab(0);
    }
  }, [activeTab, availableTabs.length]);

  const getCurrentTabValue = () => {
    return availableTabs[activeTab]?.value ?? 0;
  };

  useEffect(() => {
    logSettingsEvent("available tabs updated", {
      availableTabs: availableTabs.map((tab) => ({
        label: tab.label,
        value: tab.value,
      })),
    });
  }, [availableTabs, activeTab]);

  useEffect(() => {
    if (organizationId) {
      fetchSettings(organizationId);
    }
  }, [organizationId, fetchSettings]);

  const initialFormData = {
    dateFormat: "DD/MM/YYYY",
    timeFormat: "12hr",
    theme: "Light",
    colorPalette: "#20A8E0",
    defaultMapView: "Satellite",
    defaultApplication: "vehicleTrack",
    fontStyle: "Roboto",
    // defaultMapLayers: ["Traffic"],
    mapDetails: [],
    defaultLanguage: "en",
    trackingPageLanguages: ["en"],
    multilingualLanguages: ["en"],
    languageSwitchInterval: 20,
    maxSpeed: "",
    selectedSite: "",
    tripDuration: 60,
    reportFrequencyDays: 30,
    dataRetentionPeriod: "30",
    selectedReportTypes: ["activity", "overspeed"],
    selectedGraphs: [],
    inactiveTime: 5,
    deadTime: 10,
    vehiclesCount: 5,
    vehiclesStatus: ["active", "inactive", "dead"],
    onlyTowardsDestination: false,
    selectedReceivers: [],
    nextStopDistance: 200,
    vehicleArrivingDistance: 100,
    vehicleArrivedDistance: 50,
    vehicleDeviationDistance: 200,
    historyPointInterval: 30,
    warningMargin: 5,
    deviceInactiveMinutes: 10,
    historyPointDistanceInterval: 100,
    includeIdleTimeInETA: false,
    showStopQRCode: false,
    delayThresholdSeconds: 1,
    isDarkMode: false,
    vehicleColorIdle: "#808080",
    vehicleColorGoingToDestination: "#00FF00",
    vehicleColorGoingToOrigin: "#0000FF",
    vehicleIcons: "",
    stopOriginIcons: "",
    stopDestinationIcons: "",
    intermediateStopsIcons: "",
    chargingPointIcons: "",
    railwayStationIcons: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [originalData, setOriginalData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [isCreated, setIsCreated] = useState(false);
  const [defaultApplicationOptions, setDefaultApplicationOptions] = useState([]);

  const logSettingsEvent = (message, details = {}) => {
    console.log(`${SETTINGS_LOG_PREFIX} ${message}`, {
      activeTab,
      currentTabValue: getCurrentTabValue(),
      canAccessAnalyticsSettings,
      organizationId,
      ...details,
    });
  };

  useEffect(() => {
    const organizationTypes = Array.isArray(state?.organization?.organizationType)
      ? state.organization.organizationType
      : state?.organization?.organizationType
        ? [state.organization.organizationType]
        : [];

    setDefaultApplicationOptions(getDefaultApplicationOptions(organizationTypes));
  }, [state?.organization?.organizationType]);

  useEffect(() => {
    if (!loading && settings) {
      const mappedSettings = {
        dateFormat: settings.dateFormat || initialFormData.dateFormat,
        timeFormat: settings.timeFormat || initialFormData.timeFormat,
        theme:
          settings.theme &&
          settings.theme.charAt(0).toUpperCase() +
            settings.theme.slice(1).toLowerCase(),
        colorPalette: settings.colorTheme || initialFormData.colorPalette,
        defaultMapView:
          settings.mapview &&
          settings.mapview.charAt(0).toUpperCase() +
            settings.mapview.slice(1).toLowerCase(),
        fontStyle: settings.fontStyle || initialFormData.fontStyle,
        // defaultMapLayers: settings.mapLayers
        //   ? Array.isArray(settings.mapLayers)
        //     ? settings.mapLayers.map((layer) => formatMapLayer(layer))
        //     : [formatMapLayer(settings.mapLayers)]
        //   : initialFormData.defaultMapLayers,
        mapDetails: settings.mapDetails
          ? Array.isArray(settings.mapDetails)
            ? settings.mapDetails.map((detail) =>
                detail.toUpperCase().replace(/ /g, "_"),
              ) // Format from backend
            : []
          : initialFormData.mapDetails,
        defaultLanguage:
          settings.defaultLanguage || initialFormData.defaultLanguage,
        defaultApplication:
          settings.defaultApplication || initialFormData.defaultApplication,
        trackingPageLanguages:
          settings.multilingualLanguages &&
          Array.isArray(settings.multilingualLanguages)
            ? settings.multilingualLanguages
            : initialFormData.trackingPageLanguages,
        languageSwitchInterval:
          settings.languageSwitchInterval ||
          initialFormData.languageSwitchInterval,
        maxSpeed: settings.maxSpeed || initialFormData.maxSpeed,
        selectedSite:
          settings.siteId ||
          settings.selectedSite ||
          initialFormData.selectedSite,
        tripDuration: settings.tripDuration || initialFormData.tripDuration,
        dataRetentionPeriod:
          settings.dataRetentionPeriod || initialFormData.dataRetentionPeriod,
        reportFrequencyDays:
          settings.reportFrequencyDays || initialFormData.reportFrequencyDays,
        selectedReportTypes:
          settings.selectedReportTypes || initialFormData.selectedReportTypes,
        selectedGraphs:
          settings.selectedGraphs || initialFormData.selectedGraphs,
        inactiveTime: settings.inactiveTime || initialFormData.inactiveTime,
        deadTime: settings.deadTime || initialFormData.deadTime,
        vehiclesCount: settings.vehiclesCount || initialFormData.vehiclesCount,
        vehiclesStatus:
          settings.vehiclesStatus && Array.isArray(settings.vehiclesStatus)
            ? settings.vehiclesStatus.map(
                (status) =>
                  status.charAt(0).toLowerCase() +
                  status.slice(1).toLowerCase(),
              )
            : initialFormData.vehiclesStatus,
        onlyTowardsDestination:
          settings.onlyTowardsDestination ||
          initialFormData.onlyTowardsDestination,
        selectedReceivers:
          settings.selectedReceivers || initialFormData.selectedReceivers,
        multilingualLanguages:
          settings.multilingualLanguages ||
          initialFormData.multilingualLanguages,
        nextStopDistance:
          settings.nextStopDistance || initialFormData.nextStopDistance,
        vehicleArrivingDistance:
          settings.vehicleArrivingDistance ||
          initialFormData.vehicleArrivingDistance,
        vehicleArrivedDistance:
          settings.vehicleArrivedDistance ||
          initialFormData.vehicleArrivedDistance,
        vehicleDeviationDistance:
          settings.vehicleDeviationDistance ||
          initialFormData.vehicleDeviationDistance,
        historyPointInterval:
          settings.historyPointInterval || initialFormData.historyPointInterval,
        warningMargin:
          settings.warningMargin !== "" &&
          settings.warningMargin !== null &&
          settings.warningMargin !== undefined
            ? Number(settings.warningMargin)
            : initialFormData.warningMargin,
        deviceInactiveMinutes:
          settings.deviceInactiveMinutes !== "" &&
          settings.deviceInactiveMinutes !== null &&
          settings.deviceInactiveMinutes !== undefined
            ? Number(settings.deviceInactiveMinutes)
            : initialFormData.deviceInactiveMinutes,
        historyPointDistanceInterval:
          settings.historyDistanceInterval ??
          settings.historyPointDistanceInterval ??
          initialFormData.historyPointDistanceInterval,
        includeIdleTimeInETA:
          settings.includeIdleTimeInETA ?? initialFormData.includeIdleTimeInETA,
        showStopQRCode:
          settings.showStopQRCode ?? initialFormData.showStopQRCode,
        delayThresholdSeconds:
          settings.delayThresholdSeconds !== "" &&
          settings.delayThresholdSeconds !== null &&
          settings.delayThresholdSeconds !== undefined
            ? Number(settings.delayThresholdSeconds)
            : initialFormData.delayThresholdSeconds,
        isDarkMode: settings.isDarkMode ?? initialFormData.isDarkMode,
        vehicleColorIdle:
          settings.vehicleColorIdle || initialFormData.vehicleColorIdle,
        vehicleColorGoingToDestination:
          settings.vehicleColorGoingToDestination ||
          initialFormData.vehicleColorGoingToDestination,
        vehicleColorGoingToOrigin:
          settings.vehicleColorGoingToOrigin ||
          initialFormData.vehicleColorGoingToOrigin,
        vehicleIcons: settings.vehicleIcons || "",
        stopOriginIcons: settings.stopOriginIcons || "",
        stopDestinationIcons: settings.stopDestinationIcons || "",
        intermediateStopsIcons: settings.intermediateStopsIcons || "",
        chargingPointIcons: settings.chargingPointIcons || "",
        railwayStationIcons: settings.railwayStationIcons || "",
      };

      logSettingsEvent("mapped settings loaded", {
        mappedSettings,
        rawSettings: settings,
      });
      setFormData(mappedSettings);
      setOriginalData(mappedSettings);
      setIsCreated(!!settings.id);
    }
  }, [settings, loading]);

  const isGraphSelected = (chartId) => {
    return formData.selectedGraphs.some((graph) => graph.chartId === chartId);
  };

  const handleGraphToggle = (chartConfig) => {
    setFormData((prev) => {
      const isSelected = prev.selectedGraphs.some(
        (graph) => graph.chartId === chartConfig.id,
      );

      const nextSelectedGraphs = isSelected
        ? prev.selectedGraphs.filter(
            (graph) => graph.chartId !== chartConfig.id,
          )
        : [
            ...prev.selectedGraphs,
            {
              chartId: chartConfig.id,
              type: chartConfig.chartType,
            },
          ];

      logSettingsEvent("graph selection changed", {
        chartId: chartConfig.id,
        chartType: chartConfig.chartType,
        isSelectedBeforeClick: isSelected,
        nextSelectedGraphs,
      });

      return {
        ...prev,
        selectedGraphs: nextSelectedGraphs,
      };
    });
  };

  const handleNumberFieldBlur = (fieldName) => () => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]:
        prev[fieldName] === "" ||
        prev[fieldName] === null ||
        prev[fieldName] === undefined
          ? 0
          : prev[fieldName],
    }));
  };

  const handleChange = (event) => {
    if (organizationInactive) {
      return;
    }
    const { name, value } = event.target;
    if (name === "maxSpeed" && value !== "" && Number(value) < 0) return;
    if (name === "vehiclesCount" && value !== "" && Number(value) < 0) return;
    if (name === "warningMargin" && value !== "" && Number(value) < 0) return;
    if (name === "deviceInactiveMinutes" && value !== "" && Number(value) < 0)
      return;
    if (name === "delayThresholdSeconds" && value !== "" && Number(value) < 1)
      return;

    setFormData((prev) => {
      const nextValue =
        name === "maxSpeed" ||
        name === "tripDuration" ||
        name === "reportFrequencyDays" ||
        name === "inactiveTime" ||
        name === "deadTime" ||
        name === "vehiclesCount" ||
        name === "inactiveVehicleTimeout" ||
        name === "nextStopDistanceThreshold" ||
        name === "averageStopTime" ||
        name === "languageSwitchInterval" ||
        name === "nextStopDistance" ||
        name === "vehicleArrivingDistance" ||
        name === "vehicleArrivedDistance" ||
        name === "vehicleDeviationDistance" ||
        name === "historyPointInterval" ||
        name === "warningMargin" ||
        name === "deviceInactiveMinutes" ||
        name === "delayThresholdSeconds" ||
        name === "historyPointDistanceInterval"
          ? value
            ? Number(value)
            : ""
          : value;

      logSettingsEvent("field changed", {
        fieldName: name,
        previousValue: prev[name],
        rawValue: value,
        nextValue,
      });

      return {
        ...prev,
        [name]: nextValue,
      };
    });

    if (name === "dateFormat" || name === "timeFormat") {
      const storedSettings =
        JSON.parse(localStorage.getItem("appSettings")) || {};
      storedSettings[name] = value;
      localStorage.setItem("appSettings", JSON.stringify(storedSettings));
    }
  };

  const handleToggleChange = (fieldName) => (event) => {
    logSettingsEvent("toggle changed", {
      fieldName,
      nextValue: event.target.checked,
    });
    setFormData((prev) => ({
      ...prev,
      [fieldName]: event.target.checked,
    }));
  };

  const handleTrackingLanguagesChange = (event) => {
    const value = event.target.value;
    const nextValue = typeof value === "string" ? value.split(",") : value;
    logSettingsEvent("tracking languages changed", {
      nextValue,
    });
    setFormData((prev) => ({
      ...prev,
      trackingPageLanguages: nextValue,
    }));
  };

  const handleMultilingualLanguagesChange = (event) => {
    const value = event.target.value;
    const nextValue = typeof value === "string" ? value.split(",") : value;
    logSettingsEvent("multilingual languages changed", {
      nextValue,
    });
    setFormData((prev) => ({
      ...prev,
      multilingualLanguages: nextValue,
    }));
  };

  const fetchAllSites = async () => {
    if (!organizationId) return;

    try {
      setIsLoadingSites(true);
      const response = await axios.get(
        `${config.baseURL}/site/organization?organizationId=${organizationId}`,
        { withCredentials: true },
      );

      setSites(response.data || []);
      logSettingsEvent("sites fetched", {
        sitesCount: response.data?.length || 0,
        sites: response.data || [],
      });

      if (response.data && response.data.length > 0 && !formData.selectedSite) {
        logSettingsEvent("auto-selecting first site", {
          selectedSite: response.data[0].id || response.data[0].siteId,
        });
        setFormData((prev) => ({
          ...prev,
          selectedSite: response.data[0].id || response.data[0].siteId,
        }));
      }
    } catch (error) {
      console.error("Error fetching sites:", error);
      // toast.error("Failed to load sites");
    } finally {
      setIsLoadingSites(false);
    }
  };

  useEffect(() => {
    if (organizationId && canAccessAnalyticsSettings) {
      fetchAllSites();
      return;
    }

    setSites([]);
    setIsLoadingSites(false);
  }, [organizationId, canAccessAnalyticsSettings]);

  const handleMapLayersChange = (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      defaultMapLayers: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const handleReportTypesChange = (event) => {
    const value = event.target.value;
    const nextValue = typeof value === "string" ? value.split(",") : value;
    logSettingsEvent("report types changed", {
      nextValue,
    });
    setFormData((prev) => ({
      ...prev,
      selectedReportTypes: nextValue,
    }));
  };

  const handleVehicleStatusChange = (event) => {
    const value = event.target.value;
    const nextValue = typeof value === "string" ? value.split(",") : value;

    // If nothing is selected, default to all options checked
    const finalValue =
      nextValue.length === 0
        ? vehicleStatusOptions.map((opt) => opt.value)
        : nextValue;

    logSettingsEvent("vehicle statuses changed", { nextValue: finalValue });
    setFormData((prev) => ({
      ...prev,
      vehiclesStatus: finalValue,
    }));
  };

  const handleLanguageChange = async (event) => {
    const selectedCode = event.target.value;
    console.log("Selected language code:", selectedCode);
    logSettingsEvent("default language changed", {
      nextValue: selectedCode,
    });

    setFormData((prev) => ({
      ...prev,
      defaultLanguage: selectedCode,
    }));

    i18n.changeLanguage(selectedCode);
    localStorage.setItem("i18nextLng", selectedCode);
  };

  const handleMapDetailsChange = (event) => {
    const value = event.target.value;
    const nextValue = typeof value === "string" ? value.split(",") : value;
    logSettingsEvent("map details changed", {
      nextValue,
    });
    setFormData((prev) => ({
      ...prev,
      mapDetails: nextValue,
    }));
  };

  const validateForm = () => {
    logSettingsEvent("validateForm started", {
      formDataSnapshot: formData,
    });

    const validDateFormats = [
      "DD/MM/YYYY",
      "MM/DD/YYYY",
      "YYYY/MM/DD",
      "DD MMM YYYY",
    ];
    const validTimeFormats = ["12hr", "24hr"];
    const validMapViews = ["Roadmap", "Satellite", "Hybrid", "Terrain"];

    const hexColorRegex = /^#([0-9A-F]{3}){1,2}$/i;
    const validLanguages = languages.map((lang) => lang.code);
    const validReportFrequencies = reportFrequencyOptions.map(
      (opt) => opt.value,
    );
    const validReportTypeValues = reportTypeOptions.map((opt) => opt.value);
    const validInactiveTimes = inactiveTimeOptions.map((opt) => opt.value);
    const validDeadTimes = deadTimeOptions.map((opt) => opt.value);
    const validVehicleStatuses = vehicleStatusOptions.map((opt) => opt.value);
    const validDistanceThresholds = distanceThresholdOptions.map(
      (opt) => opt.value,
    );
    const validArrivingStatusDistances = arrivingStatusDistanceOptions.map(
      (opt) => opt.value,
    );
    const validHistoryPointsMarkers = historyPointsMarkerOptions.map(
      (opt) => opt.value,
    );
    const validWarningMargins = warningMarginOptions.map((opt) => opt.value);
    const validDeviceInactiveMinutes = deviceInactiveMinutesOptions.map(
      (opt) => opt.value,
    );
    const validLanguageCodes = languages.map((lang) => lang.code);
    const validTripDurations = tripDurationOptions.map((opt) => opt.value);

    if (!validDateFormats.includes(formData.dateFormat)) {
      toast.error("Invalid date format");
      return false;
    }

    if (!validTimeFormats.includes(formData.timeFormat)) {
      toast.error("Invalid time format");
      return false;
    }

    if (!formData.theme || typeof formData.theme !== "string") {
      toast.error("Theme is required");
      return false;
    }

    if (!hexColorRegex.test(formData.colorPalette)) {
      toast.error("Invalid color format");
      return false;
    }

    if (!validMapViews.includes(formData.defaultMapView)) {
      toast.error("Invalid map view");
      return false;
    }

    if (!formData.fontStyle) {
      toast.error("Font style is required");
      return false;
    }

    if (!validLanguages.includes(formData.defaultLanguage)) {
      toast.error("Invalid language");
      return false;
    }

    if (
      !formData.trackingPageLanguages ||
      formData.trackingPageLanguages.length === 0
    ) {
      toast.error("At least one language must be selected for tracking pages");
      return false;
    }

    const invalidLanguages = formData.trackingPageLanguages.filter(
      (lang) => !validLanguageCodes.includes(lang),
    );

    if (invalidLanguages.length > 0) {
      toast.error(`Invalid languages: ${invalidLanguages.join(", ")}`);
      return false;
    }

    const validDurations = languageChangeDurationOptions.map(
      (opt) => opt.value,
    );
    if (
      formData.languageSwitchInterval &&
      !validDurations.includes(Number(formData.languageSwitchInterval))
    ) {
      toast.error("Invalid language switch interval");
      return false;
    }

    const retentionPeriod = Number(formData.dataRetentionPeriod);
    if (
      isNaN(retentionPeriod) ||
      retentionPeriod < 1 ||
      retentionPeriod > 365
    ) {
      toast.error("Data retention period must be between 1-365 days");
      return false;
    }

    if (
      formData.maxSpeed !== "" &&
      formData.maxSpeed !== null &&
      formData.maxSpeed !== undefined &&
      (isNaN(formData.maxSpeed) ||
        formData.maxSpeed < 0 ||
        formData.maxSpeed > 300)
    ) {
      toast.error("Max speed must be between 0-300 km/h");
      return false;
    }

    if (
      formData.tripDuration !== "" &&
      !validTripDurations.includes(Number(formData.tripDuration))
    ) {
      toast.error("Invalid trip duration");
      return false;
    }

    if (canAccessAnalyticsSettings) {
      if (
        formData.reportFrequencyDays !== "" &&
        !validReportFrequencies.includes(Number(formData.reportFrequencyDays))
      ) {
        toast.error("Invalid report frequency");
        return false;
      }

      if (
        formData.selectedReportTypes &&
        formData.selectedReportTypes.length > 0
      ) {
        const invalidTypes = formData.selectedReportTypes.filter(
          (type) => !validReportTypeValues.includes(type),
        );
        if (invalidTypes.length > 0) {
          toast.error(`Invalid report types: ${invalidTypes.join(", ")}`);
          return false;
        }
      }

      if (formData.selectedGraphs && formData.selectedGraphs.length > 0) {
        const validChartIds = CUSTOM_CHART_CONFIGS.map((config) => config.id);
        const validChartTypes = [
          "barGraph",
          "pieChart",
          "lineGraph",
          "multiLineGraph",
          "horizontalBarTimeline",
        ];

        for (const graph of formData.selectedGraphs) {
          if (!graph.chartId || !validChartIds.includes(graph.chartId)) {
            toast.error(`Invalid chart ID: ${graph.chartId}`);
            return false;
          }
          if (!graph.type || !validChartTypes.includes(graph.type)) {
            toast.error(`Invalid chart type: ${graph.type}`);
            return false;
          }
        }
      }
    }

    if (
      formData.inactiveTime !== "" &&
      !validInactiveTimes.includes(Number(formData.inactiveTime))
    ) {
      toast.error("Invalid inactive time");
      return false;
    }

    if (
      formData.deadTime !== "" &&
      !validDeadTimes.includes(Number(formData.deadTime))
    ) {
      toast.error("Invalid dead time");
      return false;
    }

    if (
      formData.vehiclesCount !== "" &&
      formData.vehiclesCount !== null &&
      formData.vehiclesCount !== undefined
    ) {
      const count = Number(formData.vehiclesCount);
      if (isNaN(count) || count < 1 || count > 100) {
        toast.error("Vehicles count must be between 1-100");
        return false;
      }
    }

    if (formData.vehiclesStatus && formData.vehiclesStatus.length > 0) {
      const invalidStatuses = formData.vehiclesStatus.filter(
        (status) => !validVehicleStatuses.includes(status),
      );
      if (invalidStatuses.length > 0) {
        toast.error(`Invalid vehicle statuses: ${invalidStatuses.join(", ")}`);
        return false;
      }
    }

    if (
      formData.nextStopDistanceThreshold !== "" &&
      formData.nextStopDistanceThreshold !== null &&
      formData.nextStopDistanceThreshold !== undefined &&
      !validDistanceThresholds.includes(
        Number(formData.nextStopDistanceThreshold),
      )
    ) {
      toast.error("Invalid next stop distance threshold");
      return false;
    }

    if (
      formData.arrivingStatusDistance !== "" &&
      formData.arrivingStatusDistance !== null &&
      formData.arrivingStatusDistance !== undefined &&
      !validArrivingStatusDistances.includes(
        Number(formData.arrivingStatusDistance),
      )
    ) {
      toast.error("Invalid arriving status distance");
      return false;
    }

    if (
      formData.arrivedStatusDistance !== "" &&
      formData.arrivedStatusDistance !== null &&
      formData.arrivedStatusDistance !== undefined &&
      !validArrivingStatusDistances.includes(
        Number(formData.arrivedStatusDistance),
      )
    ) {
      toast.error("Invalid arrived status distance");
      return false;
    }

    if (
      formData.routeDeviationDistance !== "" &&
      formData.routeDeviationDistance !== null &&
      formData.routeDeviationDistance !== undefined &&
      !validDistanceThresholds.includes(Number(formData.routeDeviationDistance))
    ) {
      toast.error("Invalid route deviation distance");
      return false;
    }

    if (
      formData.historyPointsMarker !== "" &&
      formData.historyPointsMarker !== null &&
      formData.historyPointsMarker !== undefined &&
      !validHistoryPointsMarkers.includes(Number(formData.historyPointsMarker))
    ) {
      toast.error("Invalid history points marker");
      return false;
    }

    if (
      canAccessAnalyticsSettings &&
      formData.selectedReceivers &&
      formData.selectedReceivers.length > 0
    ) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = formData.selectedReceivers.filter((receiver) => {
        const email = typeof receiver === "string" ? receiver : receiver.email;
        return !emailRegex.test(email);
      });

      if (invalidEmails.length > 0) {
        toast.error("Some email addresses are invalid");
        return false;
      }
    }

    if (
      !formData.multilingualLanguages ||
      formData.multilingualLanguages.length === 0
    ) {
      toast.error("At least one multilingual language must be selected");
      return false;
    }

    const invalidMultilingualLangs = formData.multilingualLanguages.filter(
      (lang) => !validLanguageCodes.includes(lang),
    );
    if (invalidMultilingualLangs.length > 0) {
      toast.error(
        `Invalid multilingual languages: ${invalidMultilingualLangs.join(", ")}`,
      );
      return false;
    }

    const validSwitchIntervals = languageChangeDurationOptions.map(
      (opt) => opt.value,
    );
    if (
      formData.languageSwitchInterval &&
      !validSwitchIntervals.includes(Number(formData.languageSwitchInterval))
    ) {
      toast.error("Invalid language switch interval");
      return false;
    }

    if (
      formData.nextStopDistance &&
      (formData.nextStopDistance < 10 || formData.nextStopDistance > 5000)
    ) {
      toast.error("Next stop distance must be between 10-5000 meters");
      return false;
    }

    if (
      formData.vehicleArrivingDistance &&
      (formData.vehicleArrivingDistance < 10 ||
        formData.vehicleArrivingDistance > 5000)
    ) {
      toast.error("Vehicle arriving distance must be between 10-5000 meters");
      return false;
    }

    if (
      formData.vehicleArrivedDistance &&
      (formData.vehicleArrivedDistance < 10 ||
        formData.vehicleArrivedDistance > 5000)
    ) {
      toast.error("Vehicle arrived distance must be between 10-5000 meters");
      return false;
    }

    if (
      formData.delayThresholdSeconds !== "" &&
      formData.delayThresholdSeconds !== null &&
      formData.delayThresholdSeconds !== undefined &&
      (Number(formData.delayThresholdSeconds) < 1 ||
        Number(formData.delayThresholdSeconds) > 3600)
    ) {
      toast.error("Vehicle delay threshold must be between 1 and 3600 seconds");
      return false;
    }

    if (
      formData.vehicleDeviationDistance &&
      (formData.vehicleDeviationDistance < 10 ||
        formData.vehicleDeviationDistance > 10000)
    ) {
      toast.error("Vehicle deviation distance must be between 10-10000 meters");
      return false;
    }

    if (
      formData.historyPointInterval !== "" &&
      formData.historyPointInterval !== null &&
      formData.historyPointInterval !== undefined &&
      !historyPointsMarkerOptions.some(
        (opt) => Number(opt.value) === Number(formData.historyPointInterval),
      )
    ) {
      toast.error("Invalid history point interval");
      return false;
    }

    if (
      formData.historyPointDistanceInterval !== "" &&
      formData.historyPointDistanceInterval !== null &&
      formData.historyPointDistanceInterval !== undefined &&
      !historyPointDistanceOptions.some(
        (opt) =>
          Number(opt.value) === Number(formData.historyPointDistanceInterval),
      )
    ) {
      toast.error("Invalid history point distance interval");
      return false;
    }

    if (
      formData.warningMargin !== "" &&
      formData.warningMargin !== null &&
      formData.warningMargin !== undefined &&
      !validWarningMargins.includes(Number(formData.warningMargin))
    ) {
      toast.error("Invalid warning margin");
      return false;
    }

    if (
      formData.deviceInactiveMinutes !== "" &&
      formData.deviceInactiveMinutes !== null &&
      formData.deviceInactiveMinutes !== undefined &&
      !validDeviceInactiveMinutes.includes(
        Number(formData.deviceInactiveMinutes),
      )
    ) {
      toast.error("Invalid device inactive time");
      return false;
    }

    if (
      formData.vehicleColorIdle &&
      !hexColorRegex.test(formData.vehicleColorIdle)
    ) {
      toast.error("Invalid vehicle idle color format");
      return false;
    }

    if (
      formData.vehicleColorGoingToDestination &&
      !hexColorRegex.test(formData.vehicleColorGoingToDestination)
    ) {
      toast.error("Invalid vehicle color to destination format");
      return false;
    }

    if (
      formData.vehicleColorGoingToOrigin &&
      !hexColorRegex.test(formData.vehicleColorGoingToOrigin)
    ) {
      toast.error("Invalid vehicle color to origin format");
      return false;
    }
    // ✅ ADD THIS INSTEAD:
    if (formData.mapDetails && formData.mapDetails.length > 0) {
      const validMapDetails = [
        "HOTELS",
        "HOSPITALS",
        "RESTAURANTS",
        "STREET_NAMES",
        "SHOPPING_MALLS",
        "PARK_NAMES",
        "PETROL_PUMPS",
      ];

      const invalidDetails = formData.mapDetails.filter(
        (detail) => !validMapDetails.includes(detail),
      );

      if (invalidDetails.length > 0) {
        // toast.error(`Invalid map details: ${invalidDetails.join(", ")}`);
        return false;
      }
    }
    return true;
  };

  // ========================================
  // FIX: Changed to return empty string instead of null
  // ========================================
  const formatIconValue = (value) => {
    // Return empty string for null/undefined/empty instead of null
    // This ensures the field is always sent to backend
    if (!value || value === "") return "";
    return value;
  };

  const handleSave = async () => {
    if (organizationInactive) {
      toast.error(
        "Your organization is inactive. Contact the RouteEye admin to make changes.",
      );
      return;
    }
    console.groupCollapsed(`${SETTINGS_LOG_PREFIX} handleSave`);
    logSettingsEvent("save started", {
      isCreated,
      isFormChanged,
      changedKeys,
      formDataSnapshot: formData,
    });

    if (!isFormChanged) {
      logSettingsEvent("save aborted: no form changes detected", {
        changedKeys,
      });
      toast.info("No changes to save");
      console.groupEnd();
      return;
    }

    const isValid = validateForm();
    logSettingsEvent("validateForm completed", {
      isValid,
    });

    if (!isValid) {
      logSettingsEvent("save aborted: validation failed", {
        formDataSnapshot: formData,
      });
      console.groupEnd();
      return;
    }

    if (!organizationId) {
      logSettingsEvent("save aborted: missing organizationId");
      // toast.error("Organization ID not found");
      console.groupEnd();
      return;
    }

    setSaving(true);

    try {
      // ========================================
      // FIX: Build payload with explicit icon fields
      // Don't use spread operator with iconFields
      // ========================================
      const payload = {
        organizationId: Number(organizationId),
        dateFormat: formData.dateFormat,
        timeFormat: formData.timeFormat,
        theme: formData.theme.toUpperCase(),
        colorTheme: formData.colorPalette,
        mapview: formData.defaultMapView.toUpperCase(),
        // mapLayers: formData.defaultMapLayers
        //   .map((layer) => layer.toUpperCase().replace(/ /g, "_"))
        //   .join(","),
        mapDetails: formData.mapDetails.map((detail) =>
          detail.toUpperCase().replace(/ /g, "_"),
        ),
        fontStyle: formData.fontStyle,
        defaultLanguage: formData.defaultLanguage,
        defaultApplication: formData.defaultApplication || initialFormData.defaultApplication,
        languageSwitchInterval: formData.languageSwitchInterval || 20,
        maxSpeed:
          formData.maxSpeed !== "" && formData.maxSpeed != null
            ? formData.maxSpeed
            : 0,
        tripDuration: formData.tripDuration,
        dataRetentionPeriod: formData.dataRetentionPeriod.toString(),
        inactiveTime: formData.inactiveTime || 5,
        deadTime: formData.deadTime || 10,
        vehiclesCount:
          formData.vehiclesCount !== "" && formData.vehiclesCount != null
            ? formData.vehiclesCount
            : 0,
        vehiclesStatus: formData.vehiclesStatus.map((status) => status),
        onlyTowardsDestination: formData.onlyTowardsDestination,
        multilingualLanguages: formData.multilingualLanguages || ["en"],
        nextStopDistance: formData.nextStopDistance || 200,
        vehicleArrivingDistance: formData.vehicleArrivingDistance || 100,
        vehicleArrivedDistance: formData.vehicleArrivedDistance || 50,
        vehicleDeviationDistance: formData.vehicleDeviationDistance || 200,
        historyPointInterval: formData.historyPointInterval || 30,
        warningMargin:
          formData.warningMargin !== "" && formData.warningMargin != null
            ? formData.warningMargin
            : 5,
        deviceInactiveMinutes:
          formData.deviceInactiveMinutes !== "" &&
          formData.deviceInactiveMinutes != null
            ? formData.deviceInactiveMinutes
            : 10,
        historyDistanceInterval:
          formData.historyPointDistanceInterval || 100,
        historyPointDistanceInterval:
          formData.historyPointDistanceInterval || 100,
        includeIdleTimeInETA: formData.includeIdleTimeInETA ?? false,
        showStopQRCode: formData.showStopQRCode ?? false,
        delayThresholdSeconds:
          formData.delayThresholdSeconds !== "" &&
          formData.delayThresholdSeconds != null
            ? Number(formData.delayThresholdSeconds)
            : 1,
        isDarkMode: formData.isDarkMode ?? false,
        vehicleColorIdle: formData.vehicleColorIdle || "#808080",
        vehicleColorGoingToDestination:
          formData.vehicleColorGoingToDestination || "#00FF00",
        vehicleColorGoingToOrigin:
          formData.vehicleColorGoingToOrigin || "#0000FF",

        // ========================================
        // FIX: Add icon fields explicitly (not with spread)
        // This ensures they're always in the payload
        // ========================================
        vehicleIcons: formatIconValue(formData.vehicleIcons),
        stopOriginIcons: formatIconValue(formData.stopOriginIcons),
        intermediateStopsIcons: formatIconValue(
          formData.intermediateStopsIcons,
        ),
        stopDestinationIcons: formatIconValue(formData.stopDestinationIcons),
        chargingPointIcons: formatIconValue(formData.chargingPointIcons),
        railwayStationIcons: formatIconValue(formData.railwayStationIcons),
      };

      if (!isFieldVisible("mapDetails")) {
        delete payload.mapDetails;
      }
      if (!isFieldVisible("showStopQRCode")) {
        delete payload.showStopQRCode;
      }
      if (!isFieldVisible("historyPointInterval")) {
        delete payload.historyPointInterval;
        delete payload.historyDistanceInterval;
        delete payload.historyPointDistanceInterval;
      }
      if (!isFieldVisible("vehicleIcons")) {
        delete payload.vehicleIcons;
        delete payload.stopOriginIcons;
        delete payload.stopDestinationIcons;
        delete payload.intermediateStopsIcons;
        delete payload.chargingPointIcons;
        delete payload.railwayStationIcons;
      }

      if (canAccessAnalyticsSettings) {
        Object.assign(payload, {
          siteId: formData.selectedSite,
          reportFrequencyDays: formData.reportFrequencyDays || 30,
          selectedReportTypes: formData.selectedReportTypes || [
            "activity",
            "overspeed",
          ],
          selectedGraphs: formData.selectedGraphs || [],
          selectedReceivers: formData.selectedReceivers,
        });
      }

      logSettingsEvent("sending settings request", {
        requestMethod: isCreated ? "put" : "post",
        requestUrl: `${config.baseURL}/appSettings`,
        payload,
      });

      const response = await axios({
        method: isCreated ? "put" : "post",
        url: `${config.baseURL}/appSettings`,
        data: payload,
        withCredentials: true,
      });

      logSettingsEvent("settings request completed", {
        responseStatus: response.status,
        responseData: response.data,
      });

      if (response.status === 200 || response.status === 201) {
        const savedSettings = response.data || {};
        const updatedSettings = {
          ...payload,
          ...savedSettings,
          id: savedSettings.id ?? settings?.id,
          defaultLanguage:
            savedSettings.defaultLanguage || formData.defaultLanguage,
          defaultApplication:
            savedSettings.defaultApplication || formData.defaultApplication,
          mapDetails: savedSettings.mapDetails || payload.mapDetails,
          maxSpeed: savedSettings.maxSpeed ?? payload.maxSpeed,
          inactiveTime: savedSettings.inactiveTime ?? payload.inactiveTime,
          deadTime: savedSettings.deadTime ?? payload.deadTime,
          vehiclesCount: savedSettings.vehiclesCount ?? payload.vehiclesCount,
          vehiclesStatus:
            savedSettings.vehiclesStatus || payload.vehiclesStatus,
          onlyTowardsDestination:
            savedSettings.onlyTowardsDestination ??
            payload.onlyTowardsDestination,
          multilingualLanguages:
            savedSettings.multilingualLanguages ||
            payload.multilingualLanguages,
          nextStopDistance:
            savedSettings.nextStopDistance ?? payload.nextStopDistance,
          vehicleArrivingDistance:
            savedSettings.vehicleArrivingDistance ??
            payload.vehicleArrivingDistance,
          vehicleArrivedDistance:
            savedSettings.vehicleArrivedDistance ??
            payload.vehicleArrivedDistance,
          vehicleDeviationDistance:
            savedSettings.vehicleDeviationDistance ??
            payload.vehicleDeviationDistance,
          historyPointInterval:
            savedSettings.historyPointInterval ?? payload.historyPointInterval,
          warningMargin:
            savedSettings.warningMargin ?? payload.warningMargin,
          deviceInactiveMinutes:
            savedSettings.deviceInactiveMinutes ?? payload.deviceInactiveMinutes,
          historyPointDistanceInterval:
            savedSettings.historyDistanceInterval ??
            savedSettings.historyPointDistanceInterval ??
            payload.historyDistanceInterval ??
            payload.historyPointDistanceInterval,
          includeIdleTimeInETA:
            savedSettings.includeIdleTimeInETA ?? payload.includeIdleTimeInETA,
          showStopQRCode:
            savedSettings.showStopQRCode ?? payload.showStopQRCode,
          delayThresholdSeconds:
            savedSettings.delayThresholdSeconds ??
            payload.delayThresholdSeconds,
          isDarkMode: savedSettings.isDarkMode ?? payload.isDarkMode,
          vehicleColorIdle:
            savedSettings.vehicleColorIdle || payload.vehicleColorIdle,
          vehicleColorGoingToDestination:
            savedSettings.vehicleColorGoingToDestination ||
            payload.vehicleColorGoingToDestination,
          vehicleColorGoingToOrigin:
            savedSettings.vehicleColorGoingToOrigin ||
            payload.vehicleColorGoingToOrigin,
          vehicleIcons: savedSettings.vehicleIcons ?? payload.vehicleIcons,
          stopOriginIcons:
            savedSettings.stopOriginIcons ?? payload.stopOriginIcons,
          intermediateStopsIcons:
            savedSettings.intermediateStopsIcons ??
            payload.intermediateStopsIcons,
          stopDestinationIcons:
            savedSettings.stopDestinationIcons ?? payload.stopDestinationIcons,
          chargingPointIcons:
            savedSettings.chargingPointIcons ?? payload.chargingPointIcons,
          railwayStationIcons:
            savedSettings.railwayStationIcons ?? payload.railwayStationIcons,
        };

        if (canAccessAnalyticsSettings) {
          Object.assign(updatedSettings, {
            reportFrequencyDays:
              savedSettings.reportFrequencyDays ?? payload.reportFrequencyDays,
            selectedReportTypes:
              savedSettings.selectedReportTypes || payload.selectedReportTypes,
            selectedGraphs:
              savedSettings.selectedGraphs || payload.selectedGraphs,
            selectedReceivers:
              savedSettings.selectedReceivers || payload.selectedReceivers,
            selectedSite:
              savedSettings.siteId ?? payload.siteId ?? formData.selectedSite,
          });
        }

        toast.success("Settings saved successfully!");
        setOriginalData(formData);
        setIsCreated(true);
        updateSettings(updatedSettings);

        try {
          await fetchSettings(organizationId);
        } catch (refreshError) {
          console.error("Error refreshing settings after save:", refreshError);
        }

        // navigate(-1);
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      logSettingsEvent("settings request failed", {
        errorMessage: error.message,
        responseStatus: error.response?.status,
        responseData: error.response?.data,
      });
      toast.error(error.response?.data?.message || "Failed to save settings");

      if (error.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setSaving(false);
      console.groupEnd();
    }
  };

  const handleSaveClick = () => {
    logSettingsEvent("save button clicked", {
      saveButtonDisabled,
      changedKeys,
    });
    handleSave();
  };

  const handleCancel = () => {
    setFormData(originalData);
    updateSettings(originalData);
    i18n.changeLanguage(originalData.defaultLanguage);
    localStorage.setItem("i18nextLng", originalData.defaultLanguage);
    navigate(-1);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const changedKeys = useMemo(() => {
    const comparableFormData = { ...formData };
    const comparableOriginalData = { ...originalData };

    if (!canAccessAnalyticsSettings) {
      ANALYTICS_ONLY_KEYS.forEach((key) => {
        delete comparableFormData[key];
        delete comparableOriginalData[key];
      });
    }

    return Object.keys(comparableFormData).filter((key) => {
      const formValue = comparableFormData[key];
      const originalValue = comparableOriginalData[key];
      return !areSettingValuesEqual(formValue, originalValue);
    });
  }, [formData, originalData, canAccessAnalyticsSettings]);

  const isFormChanged = changedKeys.length > 0;
  const saveButtonDisabled =
    organizationInactive || saving || loading || !organizationId;

  useEffect(() => {
    logSettingsEvent("change detection updated", {
      isFormChanged,
      changedKeys,
    });
  }, [isFormChanged, changedKeys]);

  useEffect(() => {
    logSettingsEvent("save button state updated", {
      saveButtonDisabled,
      saveButtonReasons: {
        saving,
        loading,
        missingOrganizationId: !organizationId,
        isFormChanged,
      },
    });
  }, [
    saveButtonDisabled,
    saving,
    loading,
    organizationId,
    isFormChanged,
    changedKeys,
  ]);

  useEffect(() => {
    const fetchOrganizationUsers = async () => {
      if (!organizationId || !canAccessAnalyticsSettings) {
        setOrganizationUsers([]);
        return;
      }

      try {
        const response = await axios.get(
          `${config.baseURL}/user/organization/${organizationId}`,
          { withCredentials: true },
        );

        const usersArray = response.data?.users || [];
        setOrganizationUsers(Array.isArray(usersArray) ? usersArray : []);
      } catch (error) {
        console.error("Error fetching organization users:", error);
        setOrganizationUsers([]);
      }
    };

    fetchOrganizationUsers();
  }, [organizationId, canAccessAnalyticsSettings]);

  const handleRequestChange = () => {
    navigate(`/organization`);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const getEmailOptions = () => {
    if (!Array.isArray(organizationUsers)) {
      return [];
    }

    return organizationUsers.map((user) => ({
      email: user.email,
      name: user.name,
      isOrgUser: true,
      label: `${user.name} (${user.email})`,
    }));
  };

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
          boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
          margin: "20px 40px",
          border: "1px solid #E0E0E0",
          paddingBottom: "20px",
        }}
      >
        <Skeleton width={250} height={32} sx={{ m: 3 }} />
        <Box className="settings-container">
          {[...Array(14)].map((_, index) => (
            <SettingRowSkeleton key={index} />
          ))}
        </Box>
        <Box className="button-container">
          <Skeleton
            variant="rectangular"
            width={120}
            height={48}
            sx={{ borderRadius: "8px" }}
          />
          <Skeleton
            variant="rectangular"
            width={120}
            height={48}
            sx={{ borderRadius: "8px" }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "10px",
        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
        margin: "10px 40px",
        border: "1px solid #A5DCF3",
        padding: "40px",
        boxShadow: "-2px 2px 5px 0px #00000040",
      }}
    >
      {availableTabs.length === 0 ? (
        <Typography
          sx={{
            fontFamily: "Roboto",
            fontSize: "18px",
            color: "#414958",
          }}
        >
          {t("No settings are available for this organization")}
        </Typography>
      ) : (
      <>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          mb: 3,
          color: "#414958",
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiTabs-flexContainer": {
            gap: "0px",
          },
          "& .MuiTab-root": {
            textTransform: "none",
            fontSize: "20px",
            fontFamily: "Roboto",
            fontWeight: 500,
            lineHeight: "100%",
            letterSpacing: "0%",
            minHeight: "56px",
            border: "1px solid #95A0B3",
            color: "#666666",
            backgroundColor: "#FFFFFF",
            "&.Mui-selected": {
              backgroundColor: "#20A8E0",
              color: "#FFFFFF",
            },
            "&:first-of-type": {
              borderTopLeftRadius: "8px",
              borderBottomLeftRadius: "8px",
            },
            "&:last-of-type": {
              borderTopRightRadius: "8px",
              borderBottomRightRadius: "8px",
            },
            borderRight: "none",
            "&:last-child": {
              borderRight: "1px solid #95A0B3",
            },
          },
          "& .MuiTabs-indicator": {
            display: "none",
          },
        }}
      >
        {availableTabs.map((tab, index) => (
          <Tab key={tab.tabKey || tab.value} label={tab.label} />
        ))}
      </Tabs>

      {getCurrentTabValue() === 0 && (
        <Application
          SettingRow={SettingRow}
          formData={formData}
          handleChange={handleChange}
          saving={saving}
          handleMapLayersChange={handleMapLayersChange}
          handleMapDetailsChange={handleMapDetailsChange}
          handleTrackingLanguagesChange={handleTrackingLanguagesChange}
          handleMultilingualLanguagesChange={handleMultilingualLanguagesChange}
          languageChangeDurationOptions={languageChangeDurationOptions}
          languages={languages}
          handleRequestChange={handleRequestChange}
          handleLanguageChange={handleLanguageChange}
          backUpDuration={backUpDuration}
          defaultApplicationOptions={defaultApplicationOptions}
          isFieldVisible={isFieldVisible}
        />
      )}

      {getCurrentTabValue() === 1 && (
        <Tracking
          SettingRow={SettingRow}
          SettingTextRow={SettingTextRow}
          formData={formData}
          handleChange={handleChange}
          saving={saving}
          handleMapLayersChange={handleMapLayersChange}
          handleTrackingLanguagesChange={handleTrackingLanguagesChange}
          languageChangeDurationOptions={languageChangeDurationOptions}
          languages={languages}
          handleRequestChange={handleRequestChange}
          handleLanguageChange={handleLanguageChange}
          backUpDuration={backUpDuration}
          handleToggleChange={handleToggleChange}
          inactiveVehicleTimeoutOptions={inactiveVehicleTimeoutOptions}
          distanceThresholdOptions={distanceThresholdOptions}
          averageStopTimeOptions={averageStopTimeOptions}
          arrivingStatusDistanceOptions={arrivingStatusDistanceOptions}
          historyPointsMarkerOptions={historyPointsMarkerOptions}
          warningMarginOptions={warningMarginOptions}
          deviceInactiveMinutesOptions={deviceInactiveMinutesOptions}
          inactiveTimeOptions={inactiveTimeOptions}
          tripDurationOptions={tripDurationOptions}
          handleVehicleStatusChange={handleVehicleStatusChange}
          vehicleStatusOptions={vehicleStatusOptions}
          historyPointDistanceOptions={historyPointDistanceOptions}
          isFieldVisible={isFieldVisible}
          onMaxSpeedBlur={handleNumberFieldBlur("maxSpeed")}
          onVehiclesCountBlur={handleNumberFieldBlur("vehiclesCount")}
          onDelayThresholdBlur={() => {
            setFormData((prev) => ({
              ...prev,
              delayThresholdSeconds:
                prev.delayThresholdSeconds === "" ||
                prev.delayThresholdSeconds === null ||
                prev.delayThresholdSeconds === undefined
                  ? 1
                  : Math.max(1, Number(prev.delayThresholdSeconds) || 1),
            }));
          }}
        />
      )}

      {getCurrentTabValue() === 2 && (
        <EmailReport
          SettingRow={SettingRow}
          formData={formData}
          setFormData={setFormData}
          handleChange={handleChange}
          saving={saving}
          sites={sites}
          isLoadingSites={isLoadingSites}
          reportFrequencyOptions={reportFrequencyOptions}
          getEmailOptions={getEmailOptions}
          validateEmail={validateEmail}
          emailError={emailError}
          setEmailError={setEmailError}
          inputValue={inputValue}
          setInputValue={setInputValue}
        />
      )}

      {getCurrentTabValue() === 3 && (
        <Analytics
          formData={formData}
          isGraphSelected={isGraphSelected}
          handleGraphToggle={handleGraphToggle}
          saving={saving}
        />
      )}

      {getCurrentTabValue() === 4 && (
        <ThemeIcon
          formData={formData}
          handleChange={handleChange}
          saving={saving}
          isFieldVisible={isFieldVisible}
        />
      )}

      {getCurrentTabValue() === 5 && <Labels />}
      {getCurrentTabValue() === 6 && <Advertisements />}
      </>
      )}

      {availableTabs.length > 0 && getCurrentTabValue() !== 6 && (
      <Box className="button-container">
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={saving || loading}
          sx={{
            height: "55px",
            minWidth: "120px",
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "20px",
          }}
        >
          {t("Cancel")}
        </Button>
        {!organizationInactive && (
        <Button
          variant="contained"
          onClick={handleSaveClick}
          disabled={saveButtonDisabled}
          sx={{
            width: 120,
            height: "55px",
            fontSize: "20px",
            color: "#1F242E",
            textTransform: "none",
            borderRadius: "8px",
            padding: "8px 24px",
            background: "#F9E719",
            border: "1px solid #414958",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#f1de16ff",
            },
            "&.Mui-disabled": {
              backgroundColor: "#E6E6E6",
              borderColor: "#C8CBD0",
              cursor: "not-allowed",
              color: "#7A7A7A",
            },
          }}
        >
          {saving ? <CircularProgress size={24} /> : t("Save")}
        </Button>
        )}
      </Box>
      )}
    </Box>
  );
};

export default AppSettings;
